// Light, dark, or auto for the whole app. The choice is saved on the account
// (users.settings.appearance) and cached here in localStorage so the first
// paint, before the account loads, already has it. index.html runs the same
// resolution inline ahead of the bundle, so keep the two in step.

import { isAppearance, type Appearance } from "@breeze/core";

const KEY = "breeze:appearance";
const FALLBACK: Appearance = "dark";

/** The house theme the Worker put in the page (worker/src/look.ts), for a
 * device that has not chosen its own. */
function houseAppearance(): Appearance {
  const raw = typeof document === "undefined" ? null : document.querySelector('meta[name="breeze-house"]')?.getAttribute("content") ?? null;
  return isAppearance(raw) ? raw : FALLBACK;
}
const BG = { light: "#f6f6f8", dark: "#09090b" } as const;

export function getStoredAppearance(): Appearance {
  try {
    const raw = window.localStorage.getItem(KEY);
    if (isAppearance(raw)) return raw;
  } catch {
    // Storage is blocked: the default holds for this visit.
  }
  return houseAppearance();
}

export function setStoredAppearance(value: Appearance): void {
  try {
    window.localStorage.setItem(KEY, value);
  } catch {
    // Not remembered on this device.
  }
}

function deviceIsDark(): boolean {
  return typeof window.matchMedia === "function" && window.matchMedia("(prefers-color-scheme: dark)").matches;
}

function meta(name: string): HTMLMetaElement {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.name = name;
    document.head.appendChild(el);
  }
  return el;
}

/** Paints one choice: the data-theme the tokens key off, the color-scheme and
 * theme-color metas, and (for auto) follows the device while it is chosen. */
let stopFollowing: (() => void) | null = null;

export function applyAppearance(value: Appearance): void {
  stopFollowing?.();
  stopFollowing = null;
  const paint = (): void => {
    const theme = value === "auto" ? (deviceIsDark() ? "dark" : "light") : value;
    document.documentElement.dataset.theme = theme;
    meta("color-scheme").content = value === "auto" ? "light dark" : theme;
    meta("theme-color").content = BG[theme];
  };
  paint();
  if (value === "auto" && typeof window.matchMedia === "function") {
    const query = window.matchMedia("(prefers-color-scheme: dark)");
    query.addEventListener("change", paint);
    stopFollowing = () => query.removeEventListener("change", paint);
  }
}
